import { useCallback, Dispatch, SetStateAction, type KeyboardEvent } from "react";
import { useUncontrolledState } from "../../hooks/use-uncontrolled-state";

export interface UseStringSelectionPopoverArgs {
  selectedValues: string[];
  onToggle: (value: string) => void;
  maxCount?: number;
  searchQuery?: string;
  setSearchQuery?: Dispatch<SetStateAction<string>>;
  creator?: {
    isValidNewOption?: (inputValue: string, selectedValues: string[]) => boolean;
    onCreate: (value: string) => void;
  };
}

export const useStringSelectionPopover = ({
  selectedValues,
  onToggle,
  maxCount = Number.MAX_SAFE_INTEGER,
  searchQuery,
  setSearchQuery,
  creator,
}: UseStringSelectionPopoverArgs) => {
  const [search, setSearch] = useUncontrolledState({
    state: searchQuery ?? "",
    setState: setSearchQuery,
  });

  const handleToggle = useCallback(
    (value: string) => {
      const isSelected = selectedValues.includes(value);
      if (!isSelected && selectedValues.length >= maxCount) {
        return;
      }
      onToggle(value);
    },
    [selectedValues, onToggle, maxCount]
  );

  const handleCreate = useCallback(() => {
    if (!creator || !search.trim()) return;

    const trimmed = search.trim();
    const isValid = creator.isValidNewOption?.(trimmed, selectedValues) ?? true;
    if (!isValid) return;

    creator.onCreate(trimmed);
    setSearch("");
  }, [creator, search, selectedValues, setSearch]);

  const canCreate = !!creator && !!search.trim() && (creator.isValidNewOption?.(search.trim(), selectedValues) ?? true);

  const handleKeyDown = useCallback((e: KeyboardEvent<HTMLDivElement>) => {
    const input = e.target as HTMLInputElement;
    if (input.value === "" && e.key === "Escape") {
      input.blur();
    }
  }, []);

  return {
    search,
    setSearch,
    handleToggle,
    handleCreate,
    canCreate,
    handleKeyDown,
  };
};
